/**
 * In-thread marker for a single telematics-style activity event (empty/loaded
 * call, arrival, departure).
 *
 * Rendered inline with the driver's messages so the message history and the
 * activity read as one chronological timeline rather than two separate columns.
 * Mocked/demo data; no telematics/AS400 wiring.
 */

import CallMadeIcon from "@mui/icons-material/CallMade";
import CallReceivedIcon from "@mui/icons-material/CallReceived";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

import { accent, text } from "../colors";
import type { ActivityKind, DriverActivityEvent } from "../types";

const KIND_META: Record<ActivityKind, { color: string; label: string; icon: React.ReactNode }> = {
  empty_call: {
    color: accent.amber,
    label: "Empty call",
    icon: <Inventory2OutlinedIcon sx={{ fontSize: 15 }} />,
  },
  loaded_call: {
    color: accent.blue,
    label: "Loaded call",
    icon: <LocalShippingOutlinedIcon sx={{ fontSize: 15 }} />,
  },
  arrival: {
    color: accent.green,
    label: "Arrival",
    icon: <CallReceivedIcon sx={{ fontSize: 15 }} />,
  },
  departure: {
    color: accent.purple,
    label: "Departure",
    icon: <CallMadeIcon sx={{ fontSize: 15 }} />,
  },
};

export function ActivityEventMarker({ event }: { event: DriverActivityEvent }) {
  const meta = KIND_META[event.kind];
  return (
    <Box sx={{ display: "flex", justifyContent: "center", my: 0.25 }}>
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 0.75,
          maxWidth: "92%",
          px: 1.5,
          py: 0.625,
          borderRadius: "10px",
          bgcolor: `${meta.color}14`,
          border: `1px solid ${meta.color}44`,
        }}
      >
        <Box sx={{ display: "flex", color: meta.color }}>{meta.icon}</Box>
        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: meta.color }}>{meta.label}</Typography>
        <Typography
          sx={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 12, color: text.secondary }}
        >
          {event.timestamp}
        </Typography>
        <Typography sx={{ fontSize: 12.5, color: text.primary }}>· {event.location}</Typography>
        {event.detail && <Typography sx={{ fontSize: 12, color: text.muted }}>· {event.detail}</Typography>}
      </Box>
    </Box>
  );
}
